import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import * as fzstd from 'fzstd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareNodes, faCheck, faArrowRotateRight, faArrowRight, faArrowLeft, faCircleMinus, faFire, faBullseye, faBolt, faFlagCheckered, faStar, faLightbulb, faTrophy } from '@fortawesome/free-solid-svg-icons';
import WordInfoButton from './WordInfoButton';
import RatingChange from './RatingChange';
import { findShortestPathLength } from '../utils/hintSolver';
import { lemmaRunTooLong } from '../utils/lemmaRules';
import { WordGraph } from '../utils/wordGraph';
import { splitLetters, clampLetters, upperWord, lowerWord, foldLetters } from '../utils/letters';
import { resolveTyped } from '../utils/accentInput';
import { markEngaged, createShare, markShareSent, recordPushPrompt, recordAppInvite } from '../services/api';
import CoachingOverlay from './CoachingOverlay';
import { getPlatform, isNative } from '../utils/native';
import { buildShareText, deliverShareText, fallbackShareUrl, prefersShareSheet } from '../utils/share';
import { trackEvent } from '../utils/analytics';
import { APP_STORE_URL } from '../services/storeReview';
import { useNotifications } from '../contexts/NotificationContext';
import { useTranslation } from '../contexts/LanguageContext';
import useDialog from '../hooks/useDialog';

// Touch devices need a 'tap first' cue; keyboard users must not submit on blur.
const IS_TOUCH = typeof window !== 'undefined' && (window.matchMedia?.('(pointer: coarse)')?.matches || 'ontouchstart' in window);
const REDUCED_MOTION = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
// Full-screen layers render outside .main-content (the scroll container): iOS Safari
// otherwise clips position:fixed children to it and stacks them under the navbar.
const portalRoot = () => document.getElementById('root') || document.body;

/**
 * Pure, memoized pill rendered next to the current input row.
 * Memoized so it never re-renders on currentWord (keystroke) changes —
 * only when its gap/delta primitives actually change.
 */
const DistanceIndicator = React.memo(function DistanceIndicator({ gap, delta }) {
    const { t } = useTranslation();
    if (gap === null) return null;
    // Display "words still to enter" = edit distance - 1 (the final edit lands
    // on the target, which isn't counted as a word to still enter). Delta is
    // unchanged since it's a difference and the -1 shift cancels.
    const wordsToEnter = Math.max(0, gap - 1);
    return (
        <div
            className="distance-indicator"
            title={t('game.distance.title', { count: wordsToEnter })}
        >
            <FontAwesomeIcon icon={faBullseye} />
            <span className="distance-value">{wordsToEnter}</span>
            {delta !== 0 && (
                <span className={`distance-delta ${delta < 0 ? 'closer' : 'farther'}`}>
                    {delta < 0 ? '▼' : '▲'}{Math.abs(delta)}
                </span>
            )}
        </div>
    );
});

const STORE_URLS = {
    android: 'https://play.google.com/store/apps/details?id=com.wordlabyrinth.app&referrer=utm_source%3Dwebsite%26utm_medium%3Dvictory_card',
    ios: 'https://apps.apple.com/app/id6760615646',
};

/** 'android' | 'ios' for a phone browser visit, else null. */
function phoneWebOs() {
    const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent || '';
    if (/Android/i.test(ua)) return 'android';
    if (/iPhone|iPod/.test(ua)) return 'ios';
    return null;
}

/** Records today as a day with a win; returns how many distinct win days this browser has (last 10 kept). */
function recordWinDay() {
    try {
        const today = new Date().toISOString().slice(0, 10);
        const days = JSON.parse(localStorage.getItem('wl_win_days') || '[]').filter(d => typeof d === 'string');
        if (!days.includes(today)) days.push(today);
        const kept = days.slice(-10);
        localStorage.setItem('wl_win_days', JSON.stringify(kept));
        return kept.length;
    } catch {
        return 0;
    }
}

const Game = forwardRef(function Game(props, ref) {
    const { onGameWon, onHintUsed, onNextGame, onRetry, onPreviousGame, onWordSubmitted, onWrongWay, onExpertDifficulty, language = 'en', minSteps, streak, achievements = [], shareInfo, challengeConstraints, mode, ratingUpdate, canGoNext, canGoBack, historyData, rankProgressUpdate, maxStars = 3, initialSavedState, initialElapsedSeconds = 0, actionBar, hintEarned, isCompleted, suppressVictoryOverlayOnLoad = false, showDistanceIndicator = true, enableFirstMoveGuide = false, coachingMode = false, ladderPosition = null, feedbackSlot = null, completedSteps = null, victoryExtra = null, nextLabel = null, finalActions = null } = props;
    const { t } = useTranslation();
    const [currentWord, setCurrentWord] = useState('');
    const startWordRef = useRef(upperWord(props.initialData?.startWord) || 'START');
    const targetWordRef = useRef(upperWord(props.initialData?.targetWord) || 'TARGET');
    const [gameStatus, setGameStatus] = useState({ won: false, message: '' });
    const [previousGuesses, setPreviousGuesses] = useState([]);
    const [nextGuesses, setNextGuesses] = useState([]);
    const [dictionary, setDictionary] = useState(null); // WordGraph once loaded
    const [dictionaryLoaded, setDictionaryLoaded] = useState(false);
    const [, setDictionaryError] = useState(null);
    const [inputError, setInputError] = useState(false);
    const [buttonClass, setButtonClass] = useState('');
    const inputRef = useRef(null);
    const wordContainerRef = useRef(null);
    const currentInputRowRef = useRef(null);
    const keyboardPluginRef = useRef(null);
    // Fires once per game when the player types their first letter — the real
    // "started playing" signal for unit-economics analytics. Reset per game.
    const firstLetterSentRef = useRef(false);

    const maybeMarkEngaged = useCallback(() => {
        if (firstLetterSentRef.current) return;
        firstLetterSentRef.current = true;
        try {
            markEngaged({
                gameId: props.initialData?.id ?? props.initialData?.gameId ?? null,
                mode,
                lang: language,
                platform: getPlatform(),
            });
        } catch (e) { /* best-effort: engagement tracking must never break play */ }
    }, [props.initialData, mode, language]);
    const justWonRef = useRef(false); // Track if we just won to prevent immediate Enter navigation
    const lastGameRef = useRef({ start: '', target: '' }); // Track the last game to avoid unnecessary resets
    const timeoutRefs = useRef([]); // Track all timeouts for cleanup
    const victoryTriggeredRef = useRef(false); // Prevent duplicate victory triggers
    const isComposingRef = useRef(false); // Track IME composition state (Samsung/Android keyboards)

    // Elapsed time tracking — pauses when tab is hidden
    const accumulatedSecondsRef = useRef(initialElapsedSeconds);
    const lastResumeTimeRef = useRef(performance.now());

    // Managed setTimeout that tracks IDs for cleanup
    const safeSetTimeout = useCallback((fn, delay) => {
        const id = setTimeout(() => {
            fn();
            // Remove from tracking after execution
            timeoutRefs.current = timeoutRefs.current.filter(t => t !== id);
        }, delay);
        timeoutRefs.current.push(id);
        return id;
    }, []);

    // Cleanup all timeouts on unmount
    useEffect(() => {
        return () => {
            timeoutRefs.current.forEach(clearTimeout);
            timeoutRefs.current = [];
        };
    }, []);

    // Elapsed time: pause on visibility hidden, resume on visible
    useEffect(() => {
        const handleVisibility = () => {
            if (document.hidden) {
                // Pause: accumulate time so far
                accumulatedSecondsRef.current += (performance.now() - lastResumeTimeRef.current) / 1000;
            } else {
                // Resume: reset the resume timestamp
                lastResumeTimeRef.current = performance.now();
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, []);

    // Visual enhancement states
    const [flippingWord, setFlippingWord] = useState(null); // Track word being flipped
    const [showConfetti, setShowConfetti] = useState(false);
    const [victoryAnimating, setVictoryAnimating] = useState(false);
    const [popInIndex, setPopInIndex] = useState(-1); // Track which letter just got typed
    const [showVictoryOverlay, setShowVictoryOverlay] = useState(false); // Show victory overlay
    const [shareStatus, setShareStatus] = useState('idle'); // idle | busy | copied | shared
    const preparedShareRef = useRef(null); // { key, promise, share }
    const winElapsedRef = useRef(null);
    // "Remind me tomorrow" push opt-in, asked once (7-day cooldown) when the daily ladder is finished.
    const { pushSupported, hasBeenAsked, pushEnabled, requestPush, pushBlocker, markAsked } = useNotifications();
    const [reminderState, setReminderState] = useState(null); // null | requestPush outcome
    const dailyLadderDone = mode === 'ranked' && !!ladderPosition && ladderPosition.total > 0
        && ladderPosition.done >= ladderPosition.total;
    const pushAskVisible = gameStatus.won && showVictoryOverlay && dailyLadderDone && pushSupported && !pushEnabled
        && !hasBeenAsked && reminderState === null && pushBlocker !== 'denied';
    const pushAskLoggedRef = useRef(false);
    // Returning phone-browser players (wins on 2+ days) get a link to the app, where reminders work.
    const [appInviteOs] = useState(() => (isNative() ? null : phoneWebOs()));
    const [winDays, setWinDays] = useState(0);
    useEffect(() => {
        if (gameStatus.won) setWinDays(recordWinDay());
    }, [gameStatus.won]);
    const appInviteVisible = gameStatus.won && showVictoryOverlay && !!appInviteOs && winDays >= 2;
    const appInviteLoggedRef = useRef(false);
    useEffect(() => {
        if (!appInviteVisible || appInviteLoggedRef.current) return;
        appInviteLoggedRef.current = true;
        recordAppInvite({ step: 'shown', os: appInviteOs });
    }, [appInviteVisible, appInviteOs]);
    useEffect(() => {
        // Once per page load: how often the reminder button is actually seen.
        if (!pushAskVisible || pushAskLoggedRef.current) return;
        pushAskLoggedRef.current = true;
        recordPushPrompt({ step: 'shown', surface: 'ladder_done' });
    }, [pushAskVisible]);
    const [screenReaderMessage, setScreenReaderMessage] = useState(''); // Accessibility announcements
    const [inputFocused, setInputFocused] = useState(false);
    const [inputErrorText, setInputErrorText] = useState('');
    const [autoNext, setAutoNext] = useState(null); // seconds until auto-continue, null = off
    const lastAcceptedRef = useRef(null);
    const lastSlotMsgRef = useRef(null); // keeps the text visible while the slot animates closed
    const victoryDialogRef = useDialog(gameStatus.won && showVictoryOverlay, () => setShowVictoryOverlay(false));

    /**
     * Find differences between two words for highlighting.
     *
     * Returns:
     * - changed: indices of changed letters (same-length transformation)
     * - added: indices of newly added letters (word got longer)
     * - insertionEdge: index in curr where a letter was inserted (show left edge on this letter)
     * - removalEdge: index in curr where a letter was removed (show left edge on this letter)
     *
     * Visual highlighting rules:
     * - Same length (letter change): highlight the changed letter with gold background
     * - Word got longer (letter added): highlight the new letter AND show insertion edge
     * - Word got shorter (letter removed): show removal edge between adjacent letters
     */
    const getLetterDifferences = useCallback((refWord, currentWord) => {
        // Arrays of letters, so Croatian LJ/NJ/DŽ count as one position.
        const ref = splitLetters(upperWord(refWord), language);
        const curr = splitLetters(upperWord(currentWord), language);
        const result = {
            changed: [],      // indices of changed letters (same length)
            added: [],        // indices of added letters (curr longer)
            insertionEdge: -1, // index where letter was inserted (curr longer)
            removalEdge: -1,   // index where letter was removed (curr shorter)
        };

        if (ref.length === curr.length) {
            // Same length - find the changed letter(s)
            for (let i = 0; i < curr.length; i++) {
                if (ref[i] !== curr[i]) {
                    result.changed.push(i);
                }
            }
        } else if (curr.length === ref.length + 1) {
            // Current word is 1 longer - a letter was added
            // Find where the letter was inserted
            let refIdx = 0;
            for (let i = 0; i < curr.length; i++) {
                if (refIdx < ref.length && ref[refIdx] === curr[i]) {
                    refIdx++;
                } else {
                    // This is the inserted letter
                    result.added.push(i);
                    result.insertionEdge = i;
                }
            }
        } else if (curr.length === ref.length - 1) {
            // Current word is 1 shorter - a letter was removed from ref
            // Find where the letter was removed
            let currIdx = 0;
            for (let refIdx = 0; refIdx < ref.length; refIdx++) {
                if (currIdx < curr.length && ref[refIdx] === curr[currIdx]) {
                    currIdx++;
                } else {
                    // Letter at ref[refIdx] was removed
                    // removalEdge = currIdx means:
                    // - if currIdx < curr.length: show edge on LEFT of letter at currIdx
                    // - if currIdx === curr.length: show edge on RIGHT of last letter
                    result.removalEdge = currIdx;
                    break;
                }
            }
            // If we completed the loop without finding removal, it was at the very end
            if (result.removalEdge === -1) {
                result.removalEdge = curr.length;
            }
        }
        return result;
    }, [language]);

    /**
     * Check if two words are one edit away (connected).
     * One edit = one letter changed, added, or removed.
     */
    const wordsConnect = useCallback((word1, word2) => {
        const w1 = splitLetters(upperWord(word1), language);
        const w2 = splitLetters(upperWord(word2), language);

        // Same length: one char different
        if (w1.length === w2.length) {
            let diffs = 0;
            for (let i = 0; i < w1.length; i++) {
                if (w1[i] !== w2[i]) diffs++;
                if (diffs > 1) return false;
            }
            return diffs === 1;
        }

        // One char added (w2 is one longer than w1)
        if (w2.length === w1.length + 1) {
            let foundDiff = false;
            for (let i = 0, j = 0; i < w1.length; i++, j++) {
                if (w2[j] !== w1[i]) {
                    if (foundDiff) return false;
                    foundDiff = true;
                    i--;
                }
            }
            return true;
        }

        // One char added (w1 is one longer than w2)
        if (w1.length === w2.length + 1) {
            let foundDiff = false;
            for (let i = 0, j = 0; i < w2.length; i++, j++) {
                if (w1[j] !== w2[i]) {
                    if (foundDiff) return false;
                    foundDiff = true;
                    i--;
                }
            }
            return true;
        }

        return false;
    }, [language]);

    /**
     * Compute Levenshtein edit distance between two words.
     * Words in this game are typically 3–8 letters, so O(m*n) DP is trivial.
     */
    const editDistance = useCallback((a, b) => {
        const s1 = splitLetters(upperWord(a || ''), language);
        const s2 = splitLetters(upperWord(b || ''), language);
        const m = s1.length;
        const n = s2.length;
        if (m === 0) return n;
        if (n === 0) return m;
        const dp = new Array(n + 1);
        for (let j = 0; j <= n; j++) dp[j] = j;
        for (let i = 1; i <= m; i++) {
            let prev = dp[0];
            dp[0] = i;
            for (let j = 1; j <= n; j++) {
                const tmp = dp[j];
                dp[j] = s1[i - 1] === s2[j - 1]
                    ? prev
                    : 1 + Math.min(prev, dp[j], dp[j - 1]);
                prev = tmp;
            }
        }
        return dp[n];
    }, [language]);

    // "No-assist" bonus tracking: true if indicator was shown at any point during this game.
    // Resets when the game starts (initialData changes). Used to award +5% bonus on submit.
    const indicatorEverShownRef = useRef(showDistanceIndicator);
    useEffect(() => {
        if (showDistanceIndicator) indicatorEverShownRef.current = true;
    }, [showDistanceIndicator]);

    // Track the previous word's distance so we can detect "wrong way" submissions.
    // Populated on each successful submit to compare against the next one.
    const prevWordDistanceRef = useRef(null);

    // Distance pill state: only updated when a word is committed (or board resets).
    // Never changes while the user is typing, to keep the UI stable and fast.
    // `gap` = BFS edge count between the row just above the current input
    //         (last previous guess, or startWord) and the row just below it
    //         (first next guess, or targetWord). null hides the pill.
    // `delta` = change vs the previous committed gap (for ▼/▲ arrow).
    // `forTarget` = tracks the current target so we can reset on a new game.
    const [distanceIndicatorState, setDistanceIndicatorState] = useState({
        gap: null,
        delta: 0,
        forTarget: null,
    });

    // Recompute the gap whenever committed state (prev/next guesses) changes,
    // or when the game resets. Does NOT run on keystrokes.
    //
    // The gap is the actual word-graph BFS distance — the same algorithm the
    // hint solver uses (they share `bfsShortestPath`), so the pill's "words to
    // enter" matches what hints will actually deliver. We deliberately do NOT
    // fall back to Levenshtein edit distance: it's only a lower bound, so it
    // would (a) underreport the real ladder length and (b) seed the pill with
    // a smaller baseline than the BFS value, producing a misleading ▲ arrow
    // the moment the dictionary finishes loading.
    useEffect(() => {
        const target = targetWordRef.current;
        const start = startWordRef.current;

        const dictReady = dictionaryLoaded && dictionary && dictionary.size > 0;
        if (!dictReady) {
            // Dictionary not loaded yet — hide the pill rather than show an
            // estimate. Reset on game change so any prior delta is dropped.
            setDistanceIndicatorState(prev =>
                prev.forTarget === target
                    ? prev
                    : { gap: null, delta: 0, forTarget: target }
            );
            return;
        }

        const above = previousGuesses.length > 0
            ? previousGuesses[previousGuesses.length - 1]
            : start;
        const below = nextGuesses.length > 0 ? nextGuesses[0] : target;
        const bfsGap = findShortestPathLength(
            dictionary, above, below,
            previousGuesses, nextGuesses, start, target,
            20, language
        );

        setDistanceIndicatorState(prev => {
            const sameGame = prev.forTarget === target;
            if (bfsGap === null) {
                // No direct path within the depth cap — the player has likely
                // dead-ended and needs to backtrack. Hiding is more honest
                // than showing a number that disagrees with what hints will
                // suggest. Reset delta so it doesn't carry over.
                if (!sameGame || prev.gap !== null || prev.delta !== 0) {
                    return { gap: null, delta: 0, forTarget: target };
                }
                return prev;
            }
            // First reading for this game (or just transitioned out of the
            // null state): no delta arrow.
            if (!sameGame || prev.gap === null) {
                return { gap: bfsGap, delta: 0, forTarget: target };
            }
            return { gap: bfsGap, delta: bfsGap - prev.gap, forTarget: target };
        });
    }, [previousGuesses, nextGuesses, dictionary, dictionaryLoaded, language]);

    // Announce the accepted word plus the remaining distance once the gap is recomputed.
    useEffect(() => {
        const w = lastAcceptedRef.current;
        if (!w || gameStatus.won) return;
        lastAcceptedRef.current = null;
        const n = distanceIndicatorState.gap != null ? Math.max(0, distanceIndicatorState.gap - 1) : null;
        setScreenReaderMessage(n != null ? t('game.sr.acceptedToGo', { word: w, count: n }) : t('game.sr.accepted', { word: w }));
    }, [distanceIndicatorState, gameStatus.won, t]);

    // Auto-continue the daily ladder after a win unless the player interacts.
    const cancelAutoNext = useCallback(() => setAutoNext(null), []);
    useEffect(() => {
        const eligible = gameStatus.won && showVictoryOverlay && ladderPosition && onNextGame
            && canGoNext !== false && !feedbackSlot && mode !== 'expert';
        if (!eligible) { setAutoNext(null); return undefined; }
        setAutoNext(6);
        const id = setInterval(() => {
            setAutoNext(prev => {
                if (prev === null) { clearInterval(id); return null; }
                if (prev <= 1) {
                    clearInterval(id);
                    const blocked = [...document.querySelectorAll('.modal-overlay-base, .purchase-overlay')]
                        .some(el => !el.querySelector('.login-modal'));
                    if (!blocked) setTimeout(() => onNextGame(), 0);
                    return null;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameStatus.won, showVictoryOverlay]);

    // Generate confetti elements
    const generateConfetti = useCallback(() => {
        if (REDUCED_MOTION) return [];
        const colors = ['#f59e0b', '#22c55e', '#3b82f6', '#ec4899', '#8b5cf6', '#ef4444'];
        const confettiPieces = [];
        for (let i = 0; i < 50; i++) {
            const style = {
                left: `${Math.random() * 100}%`,
                backgroundColor: colors[Math.floor(Math.random() * colors.length)],
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
                transform: `rotate(${Math.random() * 360}deg)`,
                borderRadius: Math.random() > 0.5 ? '50%' : '0',
            };
            confettiPieces.push(<div key={i} className="confetti" style={style} />);
        }
        return confettiPieces;
    }, []);

    // Trigger victory animation
    const triggerVictoryAnimation = useCallback(() => {
        // Clear any flip animation first to prevent CSS conflicts
        setFlippingWord(null);
        setShowConfetti(true);
        setVictoryAnimating(true);
        // Onboarding has its own guided/solo/handoff flow — the parent advances
        // the step on win. Skip the blocking victory modal here so it doesn't
        // interrupt that flow (we keep the confetti as a light celebration).
        if (mode !== 'onboarding') {
            setShowVictoryOverlay(true);
        }
        safeSetTimeout(() => setShowConfetti(false), 3000);
        // Keep victory state - don't reset it (highlighting should persist)
    }, [mode]);

    // Calculate star rating based on steps vs optimal
    const calculateStars = useCallback((userSteps, optimalSteps) => {
        if (!optimalSteps || optimalSteps <= 0) return Math.min(3, maxStars);
        const diff = userSteps - optimalSteps;
        let stars;
        if (diff === 0) stars = 3;
        else if (diff === 1) stars = 2;
        else stars = 1;
        return Math.min(stars, maxStars);
    }, [maxStars]);

    // Get the user's step count (number of edges in the path)
    const getUserSteps = useCallback(() => {
        // Path: start -> previousGuesses -> nextGuesses -> target
        // Steps = edges = nodes - 1 = (1 + prev.length + next.length + 1) - 1
        const live = previousGuesses.length + nextGuesses.length + 1;
        // A game reopened as already won has an empty board; use the server's best instead.
        if (gameStatus.won && live === 1 && completedSteps > 1 && completedSteps < 999) return completedSteps;
        return live;
    }, [previousGuesses, nextGuesses, gameStatus.won, completedSteps]);

    // Render star display (number of stars shown = maxStars)
    const renderStars = (starCount) => {
        const stars = [];
        for (let i = 0; i < maxStars; i++) {
            stars.push(
                <span
                    key={i}
                    className={`victory-star ${i < starCount ? 'filled' : 'empty'}`}
                    style={{ animationDelay: `${i * 0.15}s` }}
                >
                    ★
                </span>
            );
        }
        return stars;
    };

    // Seconds on the clock when the puzzle was solved (the timer keeps running behind the overlay).
    useEffect(() => {
        if (!gameStatus.won) { winElapsedRef.current = null; return; }
        if (winElapsedRef.current === null && (previousGuesses.length || nextGuesses.length)) {
            winElapsedRef.current = Math.round(accumulatedSecondsRef.current + (performance.now() - lastResumeTimeRef.current) / 1000);
        }
    }, [gameStatus.won, previousGuesses.length, nextGuesses.length]);

    const getSolvedPath = useCallback(() => {
        if (!previousGuesses.length && !nextGuesses.length) return null; // reopened win: board is empty
        return [startWordRef.current, ...previousGuesses, ...nextGuesses, targetWordRef.current].map(w => upperWord(w));
    }, [previousGuesses, nextGuesses]);

    // Create the short link as soon as the overlay opens, so the click can share synchronously.
    const prepareShare = useCallback(() => {
        if (mode === 'onboarding') return null;
        const steps = getUserSteps();
        const key = `${startWordRef.current}|${targetWordRef.current}|${steps}`;
        if (preparedShareRef.current?.key === key) return preparedShareRef.current.promise;
        const entry = { key, share: null };
        entry.promise = createShare({
            lang: language,
            mode: shareInfo?.mode || mode,
            startWord: startWordRef.current,
            targetWord: targetWordRef.current,
            path: getSolvedPath(),
            steps,
            minSteps,
            elapsedSeconds: winElapsedRef.current,
            ladderDate: shareInfo?.ladderDate || null,
            puzzleNumber: shareInfo?.levelNumber || null,
            puzzleTotal: shareInfo?.totalGames || null,
        }).then(res => {
            entry.share = res?.share || null;
            return entry.share;
        }).catch(() => null);
        preparedShareRef.current = entry;
        return entry.promise;
    }, [mode, language, shareInfo, minSteps, getUserSteps, getSolvedPath]);

    useEffect(() => {
        if (gameStatus.won && showVictoryOverlay) prepareShare();
    }, [gameStatus.won, showVictoryOverlay, prepareShare]);

    const generateShareText = useCallback((url) => {
        const steps = getUserSteps();
        return buildShareText({
            mode: shareInfo?.mode || mode,
            ladderDate: shareInfo?.ladderDate,
            startWord: upperWord(startWordRef.current) || '',
            targetWord: upperWord(targetWordRef.current) || '',
            steps,
            minSteps,
            elapsedSeconds: winElapsedRef.current,
            streak: (shareInfo?.mode || mode) === 'ranked' ? streak : 0,
            url,
        }, t, language);
    }, [getUserSteps, minSteps, shareInfo, mode, streak, t, language]);

    const handleShare = useCallback(async () => {
        if (shareStatus === 'busy') return;
        let shared = preparedShareRef.current?.share;
        if (!shared) {
            // Link not ready yet: wait briefly, then fall back to a plain link.
            setShareStatus('busy');
            const pending = prepareShare();
            shared = pending ? await Promise.race([pending, new Promise(r => setTimeout(() => r(null), 2500))]) : null;
        }
        const text = generateShareText(shared?.url || fallbackShareUrl(shareInfo?.mode || mode));
        const result = await deliverShareText(text, { preferSheet: prefersShareSheet(isNative()), dialogTitle: t('game.share.button') });
        if (result === 'shared' || result === 'copied') {
            const method = result === 'shared' ? 'sheet' : 'copy';
            trackEvent('Share', { mode: shareInfo?.mode || mode, method, language, linked: String(!!shared) });
            if (shared?.slug) markShareSent(shared.slug, method);
            setShareStatus(result);
            safeSetTimeout(() => setShareStatus('idle'), 2500);
        } else {
            setShareStatus('idle');
        }
    }, [shareStatus, prepareShare, generateShareText, shareInfo, mode, language, safeSetTimeout, t]);

    // Calculate min and max word lengths
    const getMinLength = useCallback(() => {
        return splitLetters(previousGuesses.length > 0
            ? previousGuesses[previousGuesses.length - 1]
            : startWordRef.current, language).length;
    }, [previousGuesses, language]);

    const getMaxLength = useCallback(() => {
        return splitLetters(nextGuesses.length > 0
            ? nextGuesses[0]
            : targetWordRef.current, language).length;
    }, [nextGuesses, language]);

    useEffect(() => {
        // Load dictionary for the current language using IndexedDB (large storage)
        const DB_NAME = 'WordLabyrinthCache';
        const STORE_NAME = 'dictionaries';
        // B19: the effective version folds in the server's dictionary content-hash
        // (finalized inside fetchAndCacheDictionary once known) so a server-side
        // word-list change auto-busts the client cache — preventing the "valid word
        // rejected" desync. cacheKey/dictionaryUrl are `let` so the async version
        // resolution can update them before the cache helpers (which close over
        // them) run. Any failure keeps the base version → never blocks loading.
        const BASE_DICT_VERSION = 'v6';  // v6: cache compressed bytes, decode to a WordGraph
        let cacheKey = `dictionary_${language}_${BASE_DICT_VERSION}`;
        let dictionaryUrl = `/static/dictionary_${language}.zst?${BASE_DICT_VERSION}`;

        // Clean up old localStorage caches (migrating to IndexedDB)
        try {
            Object.keys(localStorage).forEach(key => {
                if (key.startsWith('dictionaryCache_')) {
                    localStorage.removeItem(key);
                }
            });
        } catch (e) {
            // Ignore localStorage errors
        }

        // IndexedDB helpers
        const openDB = () => {
            return new Promise((resolve, reject) => {
                const request = indexedDB.open(DB_NAME, 1);
                request.onerror = () => reject(request.error);
                request.onsuccess = () => resolve(request.result);
                request.onupgradeneeded = (event) => {
                    const db = event.target.result;
                    if (!db.objectStoreNames.contains(STORE_NAME)) {
                        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
                    }
                };
            });
        };

        const getCachedDictionary = async (key = cacheKey) => {
            try {
                const db = await openDB();
                return new Promise((resolve, reject) => {
                    const tx = db.transaction(STORE_NAME, 'readonly');
                    const store = tx.objectStore(STORE_NAME);
                    const request = store.get(key);
                    request.onerror = () => reject(request.error);
                    request.onsuccess = () => resolve(request.result?.data);
                });
            } catch (e) {
                console.warn('IndexedDB read failed:', e);
                return null;
            }
        };

        const cacheDictionary = async (data) => {
            try {
                const db = await openDB();
                return new Promise((resolve, reject) => {
                    const tx = db.transaction(STORE_NAME, 'readwrite');
                    const store = tx.objectStore(STORE_NAME);
                    const request = store.put({ key: cacheKey, data });
                    request.onerror = () => reject(request.error);
                    request.onsuccess = () => {
                        // Drop this language's older cached versions (old ones held parsed JSON, tens of MB).
                        const keys = store.getAllKeys ? store.getAllKeys() : null;
                        if (keys) {
                            keys.onsuccess = () => keys.result
                                .filter(k => typeof k === 'string' && k.startsWith(`dictionary_${language}_`) && k !== cacheKey)
                                .forEach(k => store.delete(k));
                        }
                        resolve();
                    };
                });
            } catch (e) {
                console.warn('IndexedDB write failed:', e);
            }
        };

        // Compressed dictionary bytes -> WordGraph (compact binary, or legacy JSON).
        const decodeDictionary = (compressed) => WordGraph.decode(fzstd.decompress(new Uint8Array(compressed)));

        const controller = new AbortController();

        const fetchAndCacheDictionary = async () => {
            // Check if we already have this language's dictionary loaded
            if (dictionaryLoaded && dictionary && dictionary.size > 0) {
                return;
            }

            // B19: resolve the server dictionary content-hash and fold it into the
            // cache key + URL (session-cached). Any failure keeps the base version,
            // so this can never block dictionary loading.
            // `compact` names the smaller front-coded copy (.v2.zst); without it the format-1 file is used.
            let fallbackUrl = null;
            let formatOneKey = null;
            try {
                let sv = '';
                let compact = '';
                const stored = sessionStorage.getItem(`dictver2_${language}`);
                if (stored) {
                    [sv, compact = ''] = stored.split('|');
                } else {
                    const vr = await fetch(`/api/dict-version/?lang=${language}`, { credentials: 'omit', signal: controller.signal });
                    if (vr.ok) {
                        const j = await vr.json();
                        sv = (j.version || '').slice(0, 12);
                        compact = (j.compact || '').slice(0, 12);
                        if (sv) sessionStorage.setItem(`dictver2_${language}`, `${sv}|${compact}`);
                    }
                }
                if (sv) {
                    cacheKey = `dictionary_${language}_${BASE_DICT_VERSION}-${sv}`;
                    dictionaryUrl = `/static/dictionary_${language}.zst?${BASE_DICT_VERSION}-${sv}`;
                }
                if (compact) {
                    fallbackUrl = dictionaryUrl;
                    formatOneKey = cacheKey;
                    cacheKey = `dictionary_${language}_${BASE_DICT_VERSION}-c${compact}`;
                    dictionaryUrl = `/static/dictionary_${language}.v2.zst?${BASE_DICT_VERSION}-${compact}`;
                }
            } catch (e) { /* keep base version */ }
            if (controller.signal.aborted) return;

            // Try IndexedDB cache first; a cached format-1 copy of the same version saves a re-download.
            let cachedData = await getCachedDictionary();
            if (!(cachedData && cachedData.byteLength) && formatOneKey) cachedData = await getCachedDictionary(formatOneKey);
            if (controller.signal.aborted) return;
            if (cachedData && cachedData.byteLength) {
                try {
                    setDictionary(decodeDictionary(cachedData));
                    setDictionaryLoaded(true);
                    return;
                } catch (e) {
                    console.warn('Cached dictionary unreadable, refetching:', e);
                }
            }

            // Fetch and decompress
            const download = async (url) => {
                const response = await fetch(url, { signal: controller.signal });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const bytes = await response.arrayBuffer();
                return [bytes, decodeDictionary(bytes)];
            };
            try {
                let arrayBuffer;
                let dictionaryData;
                try {
                    [arrayBuffer, dictionaryData] = await download(dictionaryUrl);
                } catch (error) {
                    if (error.name === 'AbortError' || !fallbackUrl) throw error;
                    console.warn('Compact dictionary failed, using the format-1 file:', error);
                    [arrayBuffer, dictionaryData] = await download(fallbackUrl);
                }
                if (controller.signal.aborted) return;

                // Cache the compressed bytes in IndexedDB (1-2 MB; decoding takes well under a second)
                await cacheDictionary(arrayBuffer);

                if (!controller.signal.aborted) {
                    setDictionary(dictionaryData);
                    setDictionaryLoaded(true);
                    setDictionaryError(null);
                }
            } catch (error) {
                if (error.name === 'AbortError') return;
                console.error('Error fetching dictionary:', error);
                if (!controller.signal.aborted) {
                    setDictionaryError(error.message);
                }
            }
        };

        // Reset dictionary state when language changes
        setDictionaryLoaded(false);
        setDictionary(null);
        fetchAndCacheDictionary();
        return () => controller.abort();
    }, [language]);

    useEffect(() => {
        // This effect updates the game state when new props.initialData is received
        if (props.initialData) {
            const newStart = upperWord(props.initialData.startWord);
            const newTarget = upperWord(props.initialData.targetWord);

            // Only reset game state if it's actually a different game
            // This prevents resetting when the parent re-renders with the same game
            const isSameGame = lastGameRef.current.start === newStart &&
                               lastGameRef.current.target === newTarget;

            if (!isSameGame) {
                startWordRef.current = newStart;
                targetWordRef.current = newTarget;
                setCurrentWord('');
                firstLetterSentRef.current = false;
                hasScrolledOnMountRef.current = false;
                // Reset no-assist tracking per game — snapshot the current toggle state.
                indicatorEverShownRef.current = showDistanceIndicator;
                prevWordDistanceRef.current = null;
                // Reset distance pill so the arrow doesn't carry over from the last game.
                setDistanceIndicatorState({ gap: null, delta: 0, forTarget: null });

                // History view: pre-populate solution from user path
                if (historyData?.userPath && historyData.userPath.length >= 2) {
                    const path = historyData.userPath.map(w => upperWord(w));
                    // path = [startWord, ...intermediates, targetWord]
                    // previousGuesses = intermediates before current position
                    // For a completed game, all words between start and target are "previous"
                    // and target is the last guess that connects
                    const intermediates = path.slice(1, -1); // exclude start and target
                    // Split into previous (words going forward from start) and next (words going back from target)
                    // Since this is a completed game, we just show all intermediates as previous guesses
                    setPreviousGuesses(intermediates);
                    setNextGuesses([]);
                    setGameStatus({ won: true, message: '' });
                    setVictoryAnimating(false);
                    setShowVictoryOverlay(false);
                    setShowConfetti(false);
                    victoryTriggeredRef.current = true;
                    lastGameRef.current = { start: newStart, target: newTarget };
                    justWonRef.current = true;
                    safeSetTimeout(() => { justWonRef.current = false; }, 300);
                    return; // Skip normal initialization
                }

                // Restore from saved state if available
                if (initialSavedState && initialSavedState.previousGuesses?.length + initialSavedState.nextGuesses?.length > 0) {
                    setPreviousGuesses((initialSavedState.previousGuesses || []).map(w => upperWord(w)));
                    setNextGuesses((initialSavedState.nextGuesses || []).map(w => upperWord(w)));
                    // Resuming a game already in progress — the player engaged earlier.
                    firstLetterSentRef.current = true;
                } else {
                    setPreviousGuesses([]);
                    setNextGuesses([]);
                }

                // Reset elapsed timer for this game
                accumulatedSecondsRef.current = initialElapsedSeconds;
                lastResumeTimeRef.current = performance.now();

                // If game was already completed (from server), mark as won without victory animation
                if (isCompleted) {
                    setGameStatus({ won: true, message: '' });
                    setVictoryAnimating(false);
                    setShowVictoryOverlay(false);
                    setShowConfetti(false);
                    victoryTriggeredRef.current = true;
                    // Show victory overlay after a brief delay so the board renders first.
                    // Skip the auto-pop when the parent asks us to (e.g. reopening an
                    // already-finished daily ladder) so the user isn't greeted by a
                    // victory modal on top of the completion celebration / stats card.
                    if (!suppressVictoryOverlayOnLoad) {
                        safeSetTimeout(() => {
                            setShowVictoryOverlay(true);
                        }, 400);
                    }
                } else {
                    setGameStatus({ won: false, message: '' });
                    setVictoryAnimating(false);
                    setShowVictoryOverlay(false);
                    setShowConfetti(false);
                    victoryTriggeredRef.current = false; // Reset victory trigger for new game
                }

                // Update the last game ref
                lastGameRef.current = { start: newStart, target: newTarget };

                // Prevent immediate Enter navigation when a new game loads
                // This protects against the case where a loaded game is already completed
                justWonRef.current = true;
                safeSetTimeout(() => {
                    justWonRef.current = false;
                }, 300);
            }
        }
    }, [props.initialData, initialSavedState, initialElapsedSeconds, historyData, isCompleted, suppressVictoryOverlayOnLoad]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
        getCurrentPath: () => {
            return {
                startWord: lowerWord(startWordRef.current),
                previousGuesses: previousGuesses.map(w => lowerWord(w)),
                nextGuesses: nextGuesses.map(w => lowerWord(w)),
                targetWord: lowerWord(targetWordRef.current),
            };
        },
        addHintWord: (hintWord, direction) => {
            const word = upperWord(hintWord);
            if (direction === 'forward') {
                // Add to previous guesses (from current word towards target)
                setPreviousGuesses(prev => [...prev, word]);
            } else if (direction === 'backward') {
                // Add to next guesses (from target backwards)
                setNextGuesses(prev => [word, ...prev]);
            }
            // Notify parent that hint was used
            if (onHintUsed) {
                onHintUsed(hintWord, direction);
            }
        },
        // Method to check if a word length is valid for the current game state
        isWordLengthValid: (wordLength) => {
            // Lengths in letters (Croatian LJ/NJ/DŽ count once)
            const count = (w) => splitLetters(w, language).length;
            const minLen = getMinLength();
            const maxLen = getMaxLength();
            // Allow words between min and max, inclusive
            // But also allow words slightly outside if they're part of a valid path
            return wordLength >= Math.min(minLen, count(startWordRef.current)) &&
                   wordLength <= Math.max(maxLen, count(targetWordRef.current));
        },
        getGameState: () => ({
            previousGuesses,
            nextGuesses,
            startWord: startWordRef.current,
            targetWord: targetWordRef.current,
            gameWon: gameStatus.won,
        }),
        getElapsedSeconds: () => {
            if (document.hidden) {
                return accumulatedSecondsRef.current;
            }
            return accumulatedSecondsRef.current + (performance.now() - lastResumeTimeRef.current) / 1000;
        },
        removeWordsFromTop: (count) => {
            // Remove last N words from previousGuesses
            setPreviousGuesses(prev => prev.slice(0, Math.max(0, prev.length - count)));
        },
        removeWordsFromBottom: (count) => {
            // Remove first N words from nextGuesses
            setNextGuesses(prev => prev.slice(Math.min(count, prev.length)));
        },
        // Expose dictionary for client-side hint computation
        getDictionary: () => dictionary,
        isDictionaryLoaded: () => !!(dictionaryLoaded && dictionary && dictionary.size > 0),
    }), [previousGuesses, nextGuesses, gameStatus.won, onHintUsed, dictionary, dictionaryLoaded, language, getMinLength, getMaxLength]);

    // Update button class when current word changes
    useEffect(() => {
        if (currentWord.length > 0) {
            const validation = isValidGuess(currentWord);
            updateButtonClass(validation.coloring);
        } else {
            setButtonClass('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentWord, previousGuesses, nextGuesses, dictionary]);

    // Detect native app using Capacitor API (not user-agent sniffing).
    // iPadOS 13+ sends a "Macintosh" user agent, so /iPad/ regex fails on iPad.
    const isNativeApp = !!window.Capacitor?.isNativePlatform?.();

    // Pre-load the Keyboard plugin so show()/hide() can be called synchronously
    // in click handlers. iOS requires these within the user gesture context —
    // a dynamic import() promise resolves too late.
    useEffect(() => {
        if (isNativeApp) {
            import('@capacitor/keyboard').then(({ Keyboard }) => {
                keyboardPluginRef.current = Keyboard;
            }).catch(() => {});
        }
    }, [isNativeApp]);

    // Focus the hidden input on mount and when game resets.
    // On native mobile, programmatic focus without a user gesture either won't
    // show the keyboard (iOS) or will show it unexpectedly (Android).
    // Skip auto-focus on native — keyboard opens on user tap only.
    useEffect(() => {
        // Touch web: a fresh board waits for the player's tap — programmatic focus there
        // gives iOS Safari a keyboard-less accessory bar and can scroll the page.
        const freshBoard = previousGuesses.length === 0 && nextGuesses.length === 0;
        if (IS_TOUCH && freshBoard) return;
        if (!gameStatus.won && inputRef.current && !isNativeApp) {
            inputRef.current.focus({ preventScroll: true });
            setInputFocused(document.activeElement === inputRef.current);
        }
    }, [gameStatus.won, previousGuesses, nextGuesses, isNativeApp]);

    // Auto-scroll to keep the current input row visible after user submits a guess.
    // We use a ref flag set by submitGuess() so that only user-initiated guess
    // submissions trigger a scroll — not initial mount or saved-state restoration.
    const shouldScrollAfterGuessRef = useRef(false);
    useEffect(() => {
        if (!shouldScrollAfterGuessRef.current) return;
        shouldScrollAfterGuessRef.current = false;
        if (!gameStatus.won && currentInputRowRef.current) {
            const timeoutId = setTimeout(() => {
                currentInputRowRef.current?.scrollIntoView({
                    behavior: REDUCED_MOTION ? 'auto' : 'smooth',
                    block: 'nearest'
                });
            }, 150);
            return () => clearTimeout(timeoutId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameStatus.won, previousGuesses.length, nextGuesses.length]);

    // Auto-scroll on initial load/restoration to center the active area
    const hasScrolledOnMountRef = useRef(false);
    useEffect(() => {
        if (hasScrolledOnMountRef.current) return;
        const el = currentInputRowRef.current;
        if (!el) return;
        hasScrolledOnMountRef.current = true;
        const timeoutId = setTimeout(() => {
            el.scrollIntoView({ behavior: 'instant', block: 'nearest' });
        }, 300);
        return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [previousGuesses.length, nextGuesses.length]);

    // When the keyboard shrinks the view, bring the active row (and the target row if it fits) above it.
    useEffect(() => {
        if (!inputFocused || gameStatus.won) return undefined;
        const scroller = inputRef.current?.closest('.main-content');
        const form = inputRef.current?.closest('.form-container');
        if (!scroller || !form) return undefined;
        // Native apps shrink the page for the keyboard; Android WebView's visualViewport double-counts it.
        const vv = isNativeApp ? null : window.visualViewport;
        const visibleHeight = () => Math.min(scroller.clientHeight, vv ? vv.height : Infinity);
        let lastHeight = visibleHeight();
        let pending = false;
        let timer;
        // The keyboard resizes the view in steps; act once the size has settled.
        const reveal = () => {
            const height = visibleHeight();
            if (height < lastHeight - 40) pending = true;
            lastHeight = height;
            if (!pending) return;
            clearTimeout(timer);
            timer = setTimeout(() => {
                pending = false;
                const row = currentInputRowRef.current;
                const rows = form.querySelectorAll(':scope > .word-row');
                const target = rows[rows.length - 1];
                if (!row || !target || document.activeElement !== inputRef.current) return;
                // Absolute scroll target, so repeat calls during the keyboard animation never overshoot.
                const box = scroller.getBoundingClientRect();
                const st = scroller.scrollTop;
                const top = (parseFloat(getComputedStyle(scroller).paddingTop) || 0) + 8;
                const bottom = Math.min(box.bottom, vv ? vv.offsetTop + vv.height : box.bottom) - box.top - 8;
                const rowBox = row.getBoundingClientRect();
                const rowTop = rowBox.top - box.top + st;
                const rowBottom = rowBox.bottom - box.top + st;
                const targetBottom = target.getBoundingClientRect().bottom - box.top + st;
                const wanted = Math.max(Math.min(targetBottom - bottom, rowTop - top), rowBottom - bottom);
                if (wanted > st + 1) scroller.scrollTo({ top: wanted, behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
            }, 250);
        };
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(reveal) : null;
        observer?.observe(scroller);
        vv?.addEventListener('resize', reveal);
        return () => {
            clearTimeout(timer);
            observer?.disconnect();
            vv?.removeEventListener('resize', reveal);
        };
    }, [inputFocused, gameStatus.won, isNativeApp]);

    // Keep cursor at the end of input to prevent Android keyboard issues
    // Skip during IME composition to avoid interfering with the keyboard's cursor management
    useEffect(() => {
        if (inputRef.current && !gameStatus.won && !isComposingRef.current) {
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
        }
    }, [currentWord, gameStatus.won]);

    // Check if chains connect after hint is applied (or any state change)
    // This handles the case where applying a hint completes the game
    useEffect(() => {
        // Skip if already won, victory already triggered, or no guesses made
        if (gameStatus.won || victoryTriggeredRef.current) return;
        if (previousGuesses.length === 0 && nextGuesses.length === 0) return;

        // Get the endpoints of both chains
        const lastFromStart = previousGuesses.length > 0
            ? previousGuesses[previousGuesses.length - 1]
            : startWordRef.current;
        const firstFromTarget = nextGuesses.length > 0
            ? nextGuesses[0]
            : targetWordRef.current;

        // Check if the chains connect
        if (wordsConnect(lastFromStart, firstFromTarget)) {
            // Victory! The chains connect - prevent duplicate triggers
            victoryTriggeredRef.current = true;
            setGameStatus({ won: true, message: '' });
            setScreenReaderMessage(t('game.sr.victory'));

            justWonRef.current = true;
            safeSetTimeout(() => {
                justWonRef.current = false;
            }, 500);

            // Build full path and notify parent
            const fullPath = [
                lowerWord(startWordRef.current),
                ...previousGuesses.map(w => lowerWord(w)),
                ...nextGuesses.map(w => lowerWord(w)),
                lowerWord(targetWordRef.current),
            ];

            if (onGameWon) {
                onGameWon(fullPath, {
                    distanceIndicatorUsed: indicatorEverShownRef.current,
                });
            }
        }
    }, [previousGuesses, nextGuesses, gameStatus.won, onGameWon, wordsConnect, safeSetTimeout, t]);

    // Global keyboard listener - focus input on any keypress and handle the keypress
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            // A focused control or an open modal owns the keyboard.
            const tag = e.target?.tagName;
            if (tag === 'BUTTON' || tag === 'A' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if (document.querySelector('.modal-overlay-base, .modal-overlay, .purchase-overlay')) return;
            // Handle Enter key for next level when game is won in THIS session
            // Use gameStatus.won (local state) instead of isCompleted (from server)
            // This ensures Enter only navigates after the user has actually won this game now,
            // not just because they completed it previously
            if (e.key === 'Enter' && gameStatus.won && onNextGame && !justWonRef.current) {
                e.preventDefault();
                onNextGame();
                return;
            }

            // Don't interfere with modifier keys or if focus is in an actual input
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                return;
            }

            // For letter keys, focus input and manually add the letter
            // Use Unicode letter matching to support all languages
            if (!gameStatus.won && inputRef.current && document.activeElement !== inputRef.current) {
                if (e.key.length === 1 && e.key.match(/^\p{L}$/u)) {
                    e.preventDefault();
                    const letter = foldLetters(upperWord(e.key), language);   // Russian Ё is typed as Е
                    const maxLength = getMaxLength();
                    maybeMarkEngaged();

                    // Add the letter if within max length (check inside setter to avoid stale closure).
                    // Lengths are in letters: typing J after L can turn L into LJ without a new tile.
                    setCurrentWord(prev => {
                        const next = prev + letter;
                        const count = splitLetters(next, language).length;
                        if (count <= maxLength) {
                            setPopInIndex(count - 1);
                            safeSetTimeout(() => setPopInIndex(-1), 150);
                            return next;
                        }
                        return prev;
                    });

                    // Focus the input for subsequent keypresses
                    inputRef.current.focus({ preventScroll: true });
                } else if (e.key === 'Backspace' || e.key === 'Enter') {
                    // For Backspace and Enter, just focus and let the input handler deal with it
                    inputRef.current.focus({ preventScroll: true });
                }
            }
        };

        document.addEventListener('keydown', handleGlobalKeyDown);
        return () => document.removeEventListener('keydown', handleGlobalKeyDown);
    }, [gameStatus.won, getMaxLength, onNextGame, language]);

    function updateButtonClass(validity) {
        switch (validity) {
            case 'invalid':
                setButtonClass('button-invalid');
                break;
            case 'exists':
                setButtonClass('button-exists');
                break;
            case 'valid':
                setButtonClass('button-valid');
                break;
            default:
                setButtonClass('');
        }
    }

    const handleInputChange = (event) => {
        // Handle input changes - this is the primary input path for mobile/IME keyboards
        // Note: We intentionally process onChange even during IME composition.
        // Samsung keyboards use composition for ALL input (even English letters),
        // so gating on isComposing would block all Samsung keyboard input.
        // Filter to only keep Unicode letters (IME may inject punctuation or spaces)
        // NFC first so a decomposed č (c + caron) stays one letter instead of losing its mark.
        const newValue = foldLetters(upperWord(event.target.value.normalize('NFC')), language).replace(/[^\p{L}]/gu, '');
        const maxLength = getMaxLength();

        const truncated = clampLetters(newValue, maxLength, language);

        // Check if this is an addition (letter typed)
        if (truncated.length > currentWord.length) {
            setPopInIndex(splitLetters(truncated, language).length - 1);
            safeSetTimeout(() => setPopInIndex(-1), 150);
        }
        if (truncated.length > 0) {
            maybeMarkEngaged();
        }
        setCurrentWord(truncated);
    };

    const handleCompositionStart = () => {
        isComposingRef.current = true;
    };

    const handleCompositionEnd = (event) => {
        isComposingRef.current = false;
        // Some Android browsers don't fire onChange after compositionEnd,
        // so manually sync state with the final composed value
        const newValue = foldLetters(upperWord(event.target.value.normalize('NFC')), language).replace(/[^\p{L}]/gu, '');
        const maxLength = getMaxLength();
        const truncated = clampLetters(newValue, maxLength, language);
        if (truncated.length > currentWord.length) {
            setPopInIndex(splitLetters(truncated, language).length - 1);
            safeSetTimeout(() => setPopInIndex(-1), 150);
        }
        setCurrentWord(truncated);
    };

    const handleKeyDown = (event) => {
        // Skip during IME composition (Samsung/Android virtual keyboards)
        // Android IME fires keyCode 229 for all composed input; isComposing flag also signals this
        if (event.nativeEvent?.isComposing || event.keyCode === 229 || isComposingRef.current) {
            // Still allow Enter to submit during composition
            if (event.key === 'Enter') {
                event.preventDefault();
                isComposingRef.current = false;
                submitGuess();
            }
            return;
        }

        // Ignore modifier keys alone
        const ignoredKeys = ['Shift', 'Meta', 'Control', 'Alt', 'Tab', 'CapsLock', 'Escape', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
        if (ignoredKeys.includes(event.key)) {
            return;
        }

        // Ignore if only modifier is pressed (without a letter)
        // Use Unicode letter matching to support all languages
        if ((event.metaKey || event.ctrlKey || event.altKey) && !(event.key.length === 1 && event.key.match(/^\p{L}$/u))) {
            return;
        }

        const maxLength = getMaxLength();

        if (event.key === 'Backspace') {
            event.preventDefault();
            if (currentWord.length > 0) {
                setCurrentWord(prev => prev.slice(0, -1));
            }
            return;
        }

        if (event.key === 'Enter') {
            event.preventDefault();
            submitGuess();
            return;
        }

        // Only process single letter keys (supports all Unicode letters for any language)
        if (event.key.length === 1 && event.key.match(/^\p{L}$/u)) {
            event.preventDefault();
            const letter = foldLetters(upperWord(event.key), language);

            // Don't allow more letters than max length (in letters: L then J makes one LJ tile)
            const count = splitLetters(currentWord + letter, language).length;
            if (count <= maxLength) {
                setPopInIndex(count - 1);
                setCurrentWord(prev => prev + letter);
                // Clear pop-in after animation
                safeSetTimeout(() => setPopInIndex(-1), 150);
            }
        }
    };

    // Board buttons must not steal focus: on touch devices a blur closes the keyboard.
    const keepInputFocus = (e) => { if (!gameStatus.won) e.preventDefault(); };

    const handleContainerClick = () => {
        if (inputRef.current && !gameStatus.won) {
            // On native iOS, the hidden input overlays the entire word container
            // (z-index 2, 100% width/height). Tapping a letter box actually taps
            // the input directly. iOS handles focus natively from that direct tap.
            // Calling blur()+focus() here DISRUPTS that native behavior on iOS 26+,
            // so skip it on native iOS and let iOS do its thing.
            const isIOS = window.Capacitor?.getPlatform?.() === 'ios';
            if (isNativeApp && isIOS) return;

            // On Android native, the CSS injection sets pointer-events:none and
            // z-index:-1 on the input (to fix GBoard extracted-text mode), so the
            // input can't receive direct taps. We must programmatically focus it.
            // Blur first so re-focus triggers the keyboard even if already focused.
            if (document.activeElement === inputRef.current) {
                inputRef.current.blur();
            }
            inputRef.current.focus({ preventScroll: true });

            // On Android native, programmatic focus may not show the keyboard —
            // use the Capacitor Keyboard plugin to force it open.
            if (isNativeApp && keyboardPluginRef.current) {
                keyboardPluginRef.current.show();
            }
        }
    };

    // Handle blur event for mobile keyboards - the "done" or checkmark button
    // typically just dismisses the keyboard (blur) rather than sending Enter
    const handleInputBlur = () => {
        isComposingRef.current = false; // Reset composition state on blur
        setInputFocused(false);
        if (IS_TOUCH && currentWord.length > 0 && !gameStatus.won) {
            // Only auto-submit on blur if the word is valid (avoids accidental
            // submissions when opening modals, scrolling, etc. on mobile)
            const validation = isValidGuess(currentWord);
            if (validation.valid) {
                submitGuess();
            }
        }
        // On native mobile (iOS and Android), we must NOT auto-refocus on
        // blur because programmatic focus without a user gesture either won't
        // present the keyboard (iOS) or will re-open it unexpectedly (Android).
        // On web, we keep auto-refocus so desktop users can keep typing.
        if (!gameStatus.won && !isNativeApp) {
            safeSetTimeout(() => {
                const active = document.activeElement;
                const isInteractive = active && (
                    active.tagName === 'INPUT' ||
                    active.tagName === 'TEXTAREA' ||
                    active.tagName === 'BUTTON' ||
                    active.tagName === 'A' ||
                    active.tagName === 'SELECT' ||
                    active.tagName === 'IFRAME' ||
                    active.closest('.modal-overlay') ||
                    active.closest('.victory-overlay') ||
                    active.closest('.purchase-overlay')
                );
                if (!isInteractive && inputRef.current && !gameStatus.won) {
                    inputRef.current.focus({ preventScroll: true });
                }
            }, 100);
        }
    };

    const deleteGuess = (index, type) => {
        if (type === 'prev') {
            setPreviousGuesses(prev => prev.slice(0, index));
        } else if (type === 'next') {
            setNextGuesses(prev => prev.slice(index + 1));
        }
    };

    // Helper functions to check one character difference or addition/removal
    function isOneCharDifferent(first, second) {
        const word1 = splitLetters(first, language);
        const word2 = splitLetters(second, language);
        if (word1.length !== word2.length) return false;
        let differences = 0;
        for (let i = 0; i < word1.length; i++) {
            if (word1[i] !== word2[i]) differences++;
            if (differences > 1) return false;
        }
        return differences === 1;
    }

    function isOneCharAdded(previous, added) {
        const previousWord = splitLetters(previous, language);
        const newWord = splitLetters(added, language);
        // Only allow adding a letter (newWord must be exactly 1 char longer)
        // Removing letters is NOT allowed
        if (newWord.length !== previousWord.length + 1) return false;

        let foundDifference = false;
        for (let i = 0, j = 0; i < previousWord.length; i++, j++) {
            if (newWord[j] !== previousWord[i]) {
                if (foundDifference) return false;
                foundDifference = true;
                i--;
            }
        }
        return true;
    }

    function isValidGuess(guess) {
        // Check if dictionary is loaded
        if (!dictionaryLoaded || !dictionary || dictionary.size === 0) {
            return { valid: false, reason: t('game.validation.dictionaryLoading'), coloring: 'invalid' };
        }
        const typed = guess;
        guess = resolveTyped(guess, dictionary, language);     // French ECOUTE -> ÉCOUTE

        // Lengths in letters: Croatian LJ/NJ/DŽ are one letter each.
        const guessLength = splitLetters(guess || '', language).length;
        if (!guess || guessLength < 3) {
            return { valid: false, reason: t('game.validation.tooShort'), coloring: 'invalid' };
        }

        const guessLower = lowerWord(guess);

        if (!dictionary.has(guessLower)) {
            return { valid: false, reason: t('game.validation.notInDictionary'), coloring: 'invalid' };
        }

        // Challenge constraint: minimum word length (Long Words challenge)
        if (challengeConstraints?.min_word_length && guessLength < challengeConstraints.min_word_length) {
            return { valid: false, reason: t('game.validation.minLength', { count: challengeConstraints.min_word_length }), coloring: 'exists' };
        }

        // Prevent words shorter than the starting word (show 'exists' since it's a real word)
        if (guessLength < splitLetters(startWordRef.current, language).length) {
            return { valid: false, reason: t('game.validation.shorterThanStart'), coloring: 'exists' };
        }

        // Check for duplicate words
        const allWords = [
            startWordRef.current,
            ...previousGuesses,
            ...nextGuesses,
            targetWordRef.current
        ].map(w => lowerWord(w));

        if (allWords.includes(guessLower)) {
            // DOUE resolving to the start word DOUÉ: what was typed is simply not a word.
            if (typed !== guess) return { valid: false, reason: t('game.validation.notInDictionary'), coloring: 'invalid' };
            return { valid: false, reason: t('game.validation.alreadyUsed'), coloring: 'exists' };
        }

        const lastWord = previousGuesses.length > 0
            ? upperWord(previousGuesses[previousGuesses.length - 1])
            : startWordRef.current;
        const nextWord = nextGuesses.length > 0
            ? upperWord(nextGuesses[0])
            : targetWordRef.current;

        // Check if the guess is one char different from the last or next words
        // Rules: edit one letter (same length) OR add one letter (longer by 1)
        // Removing letters is NOT allowed
        const oneCharDiffLast = isOneCharDifferent(guess, lastWord);
        const oneCharAddedLast = isOneCharAdded(lastWord, guess);  // guess is 1 longer than lastWord
        const oneCharDiffNext = isOneCharDifferent(guess, nextWord);
        const oneCharAddedNext = isOneCharAdded(guess, nextWord);  // nextWord is 1 longer than guess

        // Challenge constraint: Pure Swap — only same-length letter substitutions
        const sameLengthOnly = challengeConstraints?.same_length_only;
        const isValidWithLastWord = sameLengthOnly ? oneCharDiffLast : (oneCharDiffLast || oneCharAddedLast);
        const isValidWithNextWord = sameLengthOnly ? oneCharDiffNext : (oneCharDiffNext || oneCharAddedNext);

        if (!isValidWithLastWord && !isValidWithNextWord) {
            const reason = sameLengthOnly
                ? t('game.validation.sameLengthOnly')
                : t('game.validation.doesNotFit');
            return { valid: false, reason, coloring: 'exists' };
        }

        // Inflected languages: never three forms of one lemma in a row (kuća, kući, kumi is fine).
        // Check the window the guess would land in, including the junction when it joins both chains.
        const beforeLast = previousGuesses.length > 1
            ? previousGuesses[previousGuesses.length - 2]
            : (previousGuesses.length === 1 ? startWordRef.current : null);
        const afterNext = nextGuesses.length > 1
            ? nextGuesses[1]
            : (nextGuesses.length === 1 ? targetWordRef.current : null);
        const lemmaWindow = isValidWithLastWord && isValidWithNextWord
            ? [beforeLast, lastWord, guess, nextWord, afterNext]
            : isValidWithLastWord ? [beforeLast, lastWord, guess] : [guess, nextWord, afterNext];
        if (lemmaRunTooLong(dictionary, lemmaWindow)) {
            return { valid: false, reason: t('game.validation.lemmaRun'), coloring: 'exists' };
        }

        // If the word only matches nextWord (will be added to nextGuesses),
        // ensure it's not shorter than the last word in previousGuesses.
        // This prevents creating an impossible path where we'd need to shrink words to connect.
        if (!isValidWithLastWord && isValidWithNextWord) {
            if (guessLength < splitLetters(lastWord, language).length) {
                return { valid: false, reason: t('game.validation.shorterThanChain'), coloring: 'exists' };
            }
        }

        if (isValidWithLastWord && isValidWithNextWord) {
            return { valid: true, reason: "victory", coloring: 'valid', word: guess };
        }

        return {
            valid: true,
            reason: isValidWithLastWord ? "matchesLast" : "matchesNext",
            coloring: 'valid',
            word: guess,
        };
    }

    const submitGuess = () => {
        if (currentWord.length === 0) {
            return;
        }

        const validation = isValidGuess(currentWord);

        if (!validation.valid) {
            setInputError(true);
            setScreenReaderMessage(validation.reason || t('game.validation.invalid'));
            setInputErrorText(validation.reason || t('game.validation.notValid'));
            safeSetTimeout(() => setInputError(false), 500);
            safeSetTimeout(() => setInputErrorText(''), 2500);
            return;
        }
        const word = validation.word || currentWord;
        lastAcceptedRef.current = word;
        setInputErrorText('');

        let newPreviousGuesses = previousGuesses;
        let newNextGuesses = nextGuesses;

        // Wrong-way detection: before we mutate previousGuesses, compare the
        // submitted word's distance-to-target against the previous baseline.
        // Baseline = last previous guess, or start word if none yet.
        // Only fires for forward progress (matchesLast), not for matchesNext
        // (where the user is building from the target downwards).
        if (validation.reason === "matchesLast" && showDistanceIndicator && onWrongWay) {
            const baselineWord = previousGuesses.length > 0
                ? previousGuesses[previousGuesses.length - 1]
                : startWordRef.current;
            const submittedDist = editDistance(word, targetWordRef.current);
            const baselineDist = prevWordDistanceRef.current !== null
                ? prevWordDistanceRef.current
                : editDistance(baselineWord, targetWordRef.current);
            if (submittedDist >= baselineDist + 2) {
                onWrongWay();
            }
            prevWordDistanceRef.current = submittedDist;
        }

        shouldScrollAfterGuessRef.current = true;
        if (validation.reason === "matchesLast" || validation.reason === "victory") {
            newPreviousGuesses = [...previousGuesses, word];
            setPreviousGuesses(newPreviousGuesses);
            // Trigger flip animation for the new word
            // Timeout must be long enough for: max delay (900ms for 10 letters) + animation (500ms)
            setFlippingWord(`prev-${newPreviousGuesses.length - 1}`);
            safeSetTimeout(() => setFlippingWord(null), 1500);
        } else if (validation.reason === "matchesNext") {
            newNextGuesses = [word, ...nextGuesses];
            setNextGuesses(newNextGuesses);
            // Trigger flip animation for the new word
            // Timeout must be long enough for: max delay (900ms for 10 letters) + animation (500ms)
            setFlippingWord(`next-0`);
            safeSetTimeout(() => setFlippingWord(null), 1500);
        }

        setCurrentWord('');
        updateButtonClass('');
        setPopInIndex(-1);

        // Notify parent that a word was submitted (used to dismiss hint view)
        if (onWordSubmitted) {
            onWordSubmitted();
        }

        if (validation.reason === "victory") {
            setGameStatus({ won: true, message: '' });
            setScreenReaderMessage(t('game.sr.victory'));

            // Trigger victory animations with minimal delay
            // Small delay allows the flip animation to start but doesn't wait for full completion
            safeSetTimeout(() => triggerVictoryAnimation(), 400);

            // Prevent immediate Enter key from navigating to next game
            justWonRef.current = true;
            safeSetTimeout(() => {
                justWonRef.current = false;
            }, 500); // Allow navigation after 500ms

            // Build the full path and notify parent
            const fullPath = [
                lowerWord(startWordRef.current),
                ...newPreviousGuesses.map(w => lowerWord(w)),
                ...newNextGuesses.map(w => lowerWord(w)),
                lowerWord(targetWordRef.current),
            ];

            if (onGameWon) {
                onGameWon(fullPath, {
                    distanceIndicatorUsed: indicatorEverShownRef.current,
                });
            }
        }
    };

    // The text input.  Rendered as a full-width overlay inside the
    // word-container so Android's IME doesn't enter "extracted text"
    // mode (which only shows a floating toolbar instead of the keyboard).
    // On iOS it was previously inside the letter-box; the word-container
    // is still a user-tappable area so iOS WKWebView will present the
    // keyboard on tap.
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);

    const renderHiddenInput = () => (
        <input
            ref={inputRef}
            type="text"
            value={lowerWord(currentWord)}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onBlur={handleInputBlur}
            onFocus={() => setInputFocused(true)}
            inputMode="text"
            enterKeyHint="go"
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 1,
                fontSize: '16px',
                caretColor: 'transparent',
                color: 'transparent',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                padding: 0,
                margin: 0,
                zIndex: 2,
            }}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            aria-label={t('game.board.inputLabel', { current: currentWord || t('game.board.inputEmpty'), target: targetWordRef.current })}
        />
    );

    // Render the letter boxes for current input with change highlighting
    const renderCurrentInput = () => {
        const minLength = getMinLength();
        const maxLength = getMaxLength();
        const currentLetters = splitLetters(currentWord, language);
        const displayLength = Math.max(minLength, currentLetters.length);
        const boxes = [];

        // Uniform placeholder: in the GUIDED step, show the word to type next
        // faintly in the empty boxes — all letters the same faint style (no single
        // highlighted letter), so it reads as a clean placeholder.
        let ghostWord = null;
        if (coachingMode) {
            // Same rule as CoachingOverlay: suggest from the player's latest word, nothing once off the path.
            const sp = (props.initialData?.solutionPath || []).map(w => upperWord(String(w || '')));
            const last = previousGuesses.length ? upperWord(previousGuesses[previousGuesses.length - 1]) : sp[0];
            const mi = sp.indexOf(last);
            if (mi !== -1 && sp.length > mi + 1) {
                ghostWord = sp[mi + 1];
            }
        }

        const ghostLetters = ghostWord ? splitLetters(ghostWord, language) : null;

        // Check if current word is valid and determine what type of change it is
        const validation = currentLetters.length >= 3 ? isValidGuess(currentWord) : { valid: false };

        // Determine reference words for comparison
        const lastWord = previousGuesses.length > 0
            ? upperWord(previousGuesses[previousGuesses.length - 1])
            : startWordRef.current;
        const nextWord = nextGuesses.length > 0
            ? upperWord(nextGuesses[0])
            : targetWordRef.current;

        // Get differences if valid - check which direction
        let differences = null;
        let isReverseDirection = false;
        if (validation.valid && currentLetters.length >= 3) {
            if (validation.reason === 'matchesLast' || validation.reason === 'victory') {
                differences = getLetterDifferences(lastWord, currentWord);
            } else if (validation.reason === 'matchesNext') {
                differences = getLetterDifferences(nextWord, currentWord);
                isReverseDirection = true;
            }
        }

        for (let i = 0; i < displayLength; i++) {
            const letter = currentLetters[i] || '';
            const isActive = i === currentLetters.length && currentLetters.length < maxLength;
            const isPopIn = i === popInIndex && letter;

            // Build highlight classes based on valid word differences
            let highlightClass = '';
            if (differences && letter) {
                const isChanged = differences.changed.includes(i);
                const isAdded = differences.added.includes(i);
                // For reverse direction (matching next word), show removal edge highlights
                const isRemovalEdge = isReverseDirection && differences.removalEdge === i && differences.removalEdge < currentLetters.length;
                const isRemovalEdgeBefore = isReverseDirection && differences.removalEdge > 0 && i === differences.removalEdge - 1;
                const isRemovalEdgeEnd = isReverseDirection && differences.removalEdge === currentLetters.length && i === currentLetters.length - 1;

                if (isChanged) highlightClass += ' letter-changed';
                if (isAdded) highlightClass += ' letter-added';
                if (isRemovalEdge) highlightClass += ' removal-edge';
                if (isRemovalEdgeBefore) highlightClass += ' removal-edge-before';
                if (isRemovalEdgeEnd) highlightClass += ' removal-edge-end';
            }

            boxes.push(
                <div
                    key={i}
                    className={`letter-box ${isActive ? 'active' : ''}${isActive && inputFocused ? ' input-focused' : ''} ${inputError ? 'error' : ''}${highlightClass} ${isPopIn ? 'pop-in' : ''}${letter.length > 1 ? ' multi-letter' : ''}`}
                    onClick={handleContainerClick}
                    style={isActive ? { position: 'relative' } : undefined}
                >
                    {letter || (ghostLetters && ghostLetters[i]
                        ? <span className="ghost-letter" aria-hidden="true">{ghostLetters[i]}</span>
                        : '')}
                </div>
            );
        }

        // Add one empty box at the end if we can still add letters
        if (currentLetters.length < maxLength && currentLetters.length === displayLength) {
            boxes.push(
                <div
                    key={displayLength}
                    className={`letter-box active${inputFocused ? ' input-focused' : ''} ${inputError ? 'error' : ''}`}
                    onClick={handleContainerClick}
                >
                </div>
            );
        }

        return boxes;
    };

    /**
     * Render a guessed word row with change/edge highlighting.
     *
     * Highlighting types:
     * - letter-changed: gold background for changed letters (permanent)
     * - letter-changed-temp: lighter gold for temporary highlighting
     * - letter-added: gold background for newly inserted letters (permanent)
     * - letter-added-temp: lighter gold for temporary highlighting
     * - insertion-edge: glowing left border showing where letter was inserted
     * - removal-edge: glowing left border showing where letter was removed
     *
     * Permanent = comparing to predecessor (forward direction)
     * Temporary = comparing to successor (backward direction, not yet connected)
     */
    const renderGuessedWord = (guess, index, type) => {
        const highlightInfo = getHighlightInfo(index, type);
        const { predecessorWord, successorWord, isConnected } = highlightInfo;
        const isFlipping = flippingWord === `${type}-${index}`;
        const isVictory = victoryAnimating && gameStatus.won;

        // Calculate differences for permanent highlighting (predecessor comparison)
        // Shows what changed FROM predecessor TO get this word
        const permDiff = predecessorWord ? getLetterDifferences(predecessorWord, guess) : null;

        // Calculate differences for temporary highlighting (successor comparison)
        // Only used when not connected (no predecessor)
        // Reversed comparison so indices are relative to THIS word (guess)
        // Shows what will change in THIS word to get to successor
        const tempDiff = (!isConnected && successorWord) ? getLetterDifferences(successorWord, guess) : null;
        const guessLetters = splitLetters(guess, language);

        return (
            <div key={index} className="word-row" role="listitem" aria-label={t(type === 'prev' ? 'game.board.word' : 'game.board.wordBeforeTarget', { word: guess })}>
                {!gameStatus.won && (
                    <div className="button-box">
                        <button
                            onClick={() => deleteGuess(index, type)}
                            onPointerDown={keepInputFocus}
                            onMouseDown={keepInputFocus}
                            className="action-button delete-button"
                            aria-label={t('game.board.deleteGuess')}
                        >
                            &#x2715;
                        </button>
                    </div>
                )}
                <div className="word-container">
                    {guessLetters.map((letter, letterIndex) => {
                        // Permanent highlighting (from predecessor)
                        const isChanged = permDiff?.changed.includes(letterIndex) || false;
                        const isAdded = permDiff?.added.includes(letterIndex) || false;
                        // For insertion: highlight borders on letters SURROUNDING the added letter
                        // insertion-edge-before = right border on letter BEFORE the added letter
                        // insertion-edge-after = left border on letter AFTER the added letter
                        const isInsertionEdgeBefore = permDiff && permDiff.insertionEdge > 0 && letterIndex === permDiff.insertionEdge - 1;
                        const isInsertionEdgeAfter = permDiff && permDiff.insertionEdge >= 0 && letterIndex === permDiff.insertionEdge + 1;
                        // For removal: highlight borders around the gap where letter was removed
                        const isRemovalEdge = permDiff && permDiff.removalEdge === letterIndex && permDiff.removalEdge < guessLetters.length;
                        const isRemovalEdgeBefore = permDiff && permDiff.removalEdge > 0 && letterIndex === permDiff.removalEdge - 1;
                        const isRemovalEdgeEnd = permDiff && permDiff.removalEdge === guessLetters.length && letterIndex === guessLetters.length - 1;

                        // Temporary highlighting (from successor comparison, when not connected)
                        // tempDiff = getLetterDifferences(successor, guess), so indices are in GUESS
                        // - tempDiff.changed = letters in guess that differ from successor
                        // - tempDiff.added = letters in guess that don't exist in successor (will be removed going forward)
                        // - tempDiff.removalEdge = position in guess where a letter is "missing" (will be added going forward)
                        const isTempChanged = tempDiff?.changed.includes(letterIndex) || false;
                        // If guess is SHORTER than successor: show where letter will be inserted (gap in guess)
                        // removalEdge marks the gap position - show borders on letters surrounding the gap
                        // insertion-edge-before-temp = right border on letter BEFORE the gap
                        // insertion-edge-after-temp = left border on letter AT the gap position (will shift right when letter added)
                        const isTempInsertionEdgeBefore = tempDiff && tempDiff.removalEdge > 0 &&
                            letterIndex === tempDiff.removalEdge - 1;
                        const isTempInsertionEdgeAfter = tempDiff && tempDiff.removalEdge >= 0 &&
                            tempDiff.removalEdge === letterIndex && tempDiff.removalEdge < guessLetters.length;
                        const isTempInsertionEdgeEnd = tempDiff && tempDiff.removalEdge === guessLetters.length &&
                            letterIndex === guessLetters.length - 1;
                        // If guess is LONGER than successor: show which letter will be removed
                        // added[] marks the letter(s) in guess that don't exist in successor
                        const isTempRemovedLetter = tempDiff?.added.includes(letterIndex) || false;

                        // Build class names
                        const classes = [
                            'letter-box',
                            'guessed',
                            // Permanent highlighting
                            isChanged ? 'letter-changed' : '',
                            isAdded ? 'letter-added' : '',
                            isInsertionEdgeBefore ? 'insertion-edge-before' : '',
                            isInsertionEdgeAfter ? 'insertion-edge-after' : '',
                            isRemovalEdge ? 'removal-edge' : '',
                            isRemovalEdgeBefore ? 'removal-edge-before' : '',
                            isRemovalEdgeEnd ? 'removal-edge-end' : '',
                            // Temporary highlighting (lighter styling)
                            isTempChanged ? 'letter-changed-temp' : '',
                            isTempRemovedLetter ? 'letter-removed-temp' : '',
                            isTempInsertionEdgeBefore ? 'insertion-edge-before-temp' : '',
                            isTempInsertionEdgeAfter ? 'insertion-edge-after-temp' : '',
                            isTempInsertionEdgeEnd ? 'insertion-edge-end-temp' : '',
                            // Animation classes
                            isFlipping ? 'flip' : '',
                            isFlipping ? `flip-delay-${letterIndex}` : '',
                            isVictory ? 'victory victory-wave' : '',
                            letter.length > 1 ? 'multi-letter' : '',
                        ].filter(Boolean).join(' ');

                        return (
                            <div
                                key={letterIndex}
                                aria-hidden="true"
                                className={classes}
                                style={isVictory ? { animationDelay: `${letterIndex * 100}ms` } : {}}
                            >
                                {letter}
                            </div>
                        );
                    })}
                    <WordInfoButton word={guess} />
                </div>
            </div>
        );
    };

    /**
     * Render the target word with highlighting.
     * The target word's predecessor is the last word in nextGuesses (closest to target),
     * or the last word in previousGuesses (if no nextGuesses),
     * or the startWord (if no guesses at all).
     */
    const renderTargetWord = () => {
        const isVictory = victoryAnimating && gameStatus.won;
        const targetWord = targetWordRef.current;

        // Determine predecessor for target word
        // Only show highlighting if the predecessor actually CONNECTS to target
        let predecessorWord = null;
        if (nextGuesses.length > 0) {
            // Last nextGuess is closest to target - it always connects (by construction)
            predecessorWord = nextGuesses[nextGuesses.length - 1];
        } else if (previousGuesses.length > 0) {
            // No nextGuesses, only use last previousGuess if it connects to target
            const lastPrev = previousGuesses[previousGuesses.length - 1];
            if (wordsConnect(lastPrev, targetWord)) {
                predecessorWord = lastPrev;
            }
        } else {
            // No guesses at all, use startWord if it connects
            if (wordsConnect(startWordRef.current, targetWord)) {
                predecessorWord = startWordRef.current;
            }
        }

        // Calculate differences for highlighting
        const diff = predecessorWord ? getLetterDifferences(predecessorWord, targetWord) : null;
        const targetLetters = splitLetters(targetWord, language);
        const lettersBefore = [startWordRef.current, ...previousGuesses, ...nextGuesses]
            .reduce((total, w) => total + splitLetters(w, language).length, 0);

        return (
            <div className="word-row" role="listitem" aria-label={t('game.board.targetWord', { word: targetWord })}>
                <div className="word-container" id="targetWordContainer">
                    {targetLetters.map((letter, letterIndex) => {
                        // Highlighting from predecessor
                        const isChanged = diff?.changed.includes(letterIndex) || false;
                        const isAdded = diff?.added.includes(letterIndex) || false;
                        // For insertion: highlight borders on letters SURROUNDING the added letter
                        const isInsertionEdgeBefore = diff && diff.insertionEdge > 0 && letterIndex === diff.insertionEdge - 1;
                        const isInsertionEdgeAfter = diff && diff.insertionEdge >= 0 && letterIndex === diff.insertionEdge + 1;
                        const isRemovalEdge = diff && diff.removalEdge === letterIndex && diff.removalEdge < targetLetters.length;
                        const isRemovalEdgeBefore = diff && diff.removalEdge > 0 && letterIndex === diff.removalEdge - 1;
                        const isRemovalEdgeEnd = diff && diff.removalEdge === targetLetters.length && letterIndex === targetLetters.length - 1;

                        const classes = [
                            'letter-box',
                            'fixed',
                            isChanged ? 'letter-changed' : '',
                            isAdded ? 'letter-added' : '',
                            isInsertionEdgeBefore ? 'insertion-edge-before' : '',
                            isInsertionEdgeAfter ? 'insertion-edge-after' : '',
                            isRemovalEdge ? 'removal-edge' : '',
                            isRemovalEdgeBefore ? 'removal-edge-before' : '',
                            isRemovalEdgeEnd ? 'removal-edge-end' : '',
                            isVictory ? 'victory victory-wave' : '',
                            letter.length > 1 ? 'multi-letter' : '',
                        ].filter(Boolean).join(' ');

                        return (
                            <div
                                key={letterIndex}
                                aria-hidden="true"
                                className={classes}
                                style={isVictory ? { animationDelay: `${(lettersBefore + letterIndex) * 100}ms` } : {}}
                            >
                                {letter}
                            </div>
                        );
                    })}
                    <WordInfoButton word={targetWord} />
                </div>
            </div>
        );
    };

    /**
     * Get highlighting info for a guessed word.
     *
     * Returns { predecessorWord, successorWord, isConnected }
     *
     * Display order: start → prev[0] → prev[1] → ... → next[0] → next[1] → ... → target
     *
     * For FORWARD/PERMANENT highlighting: compare to predecessor (what changed to get here)
     * For BACKWARD/TEMPORARY highlighting: compare to successor (what this leads to)
     *
     * Rules:
     * - previousGuesses always compare to predecessor (permanent)
     * - nextGuesses compare to predecessor if available (permanent), else successor (temporary)
     * - next[0] is connected if it connects to last prev (or start if no prev)
     * - next[i>0] always has predecessor next[i-1]
     */
    const getHighlightInfo = (index, type) => {
        if (type === 'prev') {
            // Previous guesses: always have predecessor, always permanent
            const predecessor = index === 0 ? startWordRef.current : previousGuesses[index - 1];
            return { predecessorWord: predecessor, successorWord: null, isConnected: true };
        } else {
            // Next guesses: check if connected to determine permanent vs temporary
            const successor = index < nextGuesses.length - 1
                ? nextGuesses[index + 1]
                : targetWordRef.current;

            if (index === 0) {
                // First nextGuess: check if connected to prev chain
                const lastPrev = previousGuesses.length > 0
                    ? previousGuesses[previousGuesses.length - 1]
                    : startWordRef.current;
                const thisWord = nextGuesses[0];
                const isConnected = wordsConnect(lastPrev, thisWord);

                if (isConnected) {
                    // Connected: compare to predecessor (permanent)
                    return { predecessorWord: lastPrev, successorWord: successor, isConnected: true };
                } else {
                    // Not connected: compare to successor (temporary)
                    return { predecessorWord: null, successorWord: successor, isConnected: false };
                }
            } else {
                // Other nextGuesses: always have predecessor (next[i-1]), always permanent
                const predecessor = nextGuesses[index - 1];
                return { predecessorWord: predecessor, successorWord: successor, isConnected: true };
            }
        }
    };

    const handleOutsideClick = (e) => {
        // Dismiss keyboard when tapping outside the game input area.
        // Don't dismiss if the tap was on a button, link, or other interactive element.
        // Don't dismiss if tapping the site footer (causes flicker on iPad).
        if (e.target.closest('.site-footer')) return;
        const tag = e.target.tagName;
        const isInteractive = tag === 'BUTTON' || tag === 'A' || tag === 'INPUT' || tag === 'SELECT';
        const isInsideWordContainer = e.target.closest('.word-container') || e.target.closest('.letter-box');
        if (!isInteractive && !isInsideWordContainer && inputRef.current) {
            inputRef.current.blur();
            // Explicitly hide keyboard via pre-loaded plugin (synchronous).
            // On native, blur() alone may not dismiss the keyboard when
            // Keyboard.resize is 'none'.
            if (keyboardPluginRef.current) {
                keyboardPluginRef.current.hide();
            }
        }
    };

    const slotMsg = inputErrorText ? { kind: 'error', text: inputErrorText }
        : previousGuesses.length === 0 && nextGuesses.length === 0 && currentWord.length === 0 && (IS_TOUCH || !coachingMode)
            ? { kind: 'cue', text: t(IS_TOUCH ? 'game.board.cueTouch' : 'game.board.cueKeyboard') }
            : null;
    if (slotMsg) lastSlotMsgRef.current = slotMsg;
    const shownSlotMsg = slotMsg || lastSlotMsgRef.current;

    // Longest word on the board: narrow screens shrink the cells so every row fits.
    const lettersIn = (w) => splitLetters(w, language).length;
    const maxWordLen = Math.max(lettersIn(startWordRef.current), lettersIn(targetWordRef.current), lettersIn(currentWord),
        ...previousGuesses.map(lettersIn), ...nextGuesses.map(lettersIn));

    const submitValidation = lettersIn(currentWord) >= 3 ? isValidGuess(currentWord) : null;
    const submitLabel = !currentWord.length ? t('game.board.submit')
        : submitValidation?.valid ? t('game.board.submitWord', { word: submitValidation.word || currentWord })
        : t('game.board.notSubmittable', { reason: submitValidation?.reason || t('game.board.keepTyping') });

    return (
        <div className="game-page" onClick={handleOutsideClick}>
            {/* Confetti celebration */}
            {showConfetti && createPortal(
                <div className="confetti-container">
                    {generateConfetti()}
                </div>,
                portalRoot()
            )}

            {/* Visually-hidden input rendered inside the game container.
                On iOS the input must be tappable (no pointerEvents:none)
                and positioned inside the viewport for WKWebView to
                present the software keyboard. */}

            {/* Screen reader announcements */}
            <div aria-live="assertive" aria-atomic="true" className="sr-only">
                {screenReaderMessage}
            </div>

            <div className="game-container">
                {actionBar}
                <CoachingOverlay
                    active={enableFirstMoveGuide}
                    coached={coachingMode}
                    solutionPath={props.initialData?.solutionPath}
                    coachingHints={props.initialData?.coachingHints}
                    previousGuesses={previousGuesses}
                    currentWord={currentWord}
                    won={gameStatus.won}
                />
                <div className="game-center-container">
                    <div className={`form-container${victoryAnimating ? ' victory-active' : ''}${maxWordLen >= 7 ? ' long-words' : ''}`} style={{ '--max-len': maxWordLen }} role="list" aria-label={t('game.board.ladder')}>
                        {/* Start Word */}
                        <div className="word-row" role="listitem" aria-label={t('game.board.startWord', { word: startWordRef.current })}>
                            <div className="word-container">
                                {splitLetters(startWordRef.current, language).map((letter, index) => (
                                    <div
                                        className={`letter-box fixed ${victoryAnimating ? 'victory victory-wave' : ''}${letter.length > 1 ? ' multi-letter' : ''}`}
                                        key={index}
                                        aria-hidden="true"
                                        style={victoryAnimating ? { animationDelay: `${index * 100}ms` } : {}}
                                    >
                                        {letter}
                                    </div>
                                ))}
                                <WordInfoButton word={startWordRef.current} />
                            </div>
                        </div>

                        {/* Previous Guesses */}
                        <div id="previousGuessesContainer">
                            {previousGuesses.map((guess, index) =>
                                renderGuessedWord(guess, index, 'prev')
                            )}
                        </div>

                        {/* Current Guess */}
                        {!gameStatus.won && (
                            <div className="word-row" ref={currentInputRowRef} role="listitem" aria-label={t('game.board.yourGuess')}>
                                <div className="button-box">
                                    <button
                                        onClick={submitGuess}
                                        onPointerDown={keepInputFocus}
                                        onMouseDown={keepInputFocus}
                                        className={`action-button ${buttonClass}`}
                                        aria-label={submitLabel}
                                        title={submitLabel}
                                    >
                                        &#x2713;
                                    </button>
                                </div>
                                <div className="word-container" ref={wordContainerRef} onClick={handleContainerClick} style={{ position: 'relative' }}>
                                    {renderCurrentInput()}
                                    {!gameStatus.won && renderHiddenInput()}
                                </div>
                                {showDistanceIndicator && (
                                    <DistanceIndicator
                                        gap={distanceIndicatorState.gap}
                                        delta={distanceIndicatorState.delta}
                                    />
                                )}
                            </div>
                        )}
                        {/* Slot eases open/closed; the text is an overlay so the board never shifts sideways. */}
                        {!gameStatus.won && (
                            <div className={`move-msg-slot${slotMsg ? ' open' : ''}`} aria-hidden="true">
                                {shownSlotMsg && (
                                    <div className={shownSlotMsg.kind === 'error' ? 'input-error-text' : 'first-move-cue'}>
                                        <span className="msg-text">{shownSlotMsg.text}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Next Guesses */}
                        <div id="nextGuessesContainer">
                            {nextGuesses.map((guess, index) =>
                                renderGuessedWord(guess, index, 'next')
                            )}
                        </div>

                        {/* Target Word */}
                        {renderTargetWord()}
                    </div>
                </div>
                {/* Shown from the win on, so closing the overlay never shifts the board. */}
                {gameStatus.won && mode !== 'onboarding' && (
                    <div className="victory-reopen-row">
                        <button className="victory-reopen-button" onClick={() => setShowVictoryOverlay(true)}>
                            <FontAwesomeIcon icon={faTrophy} /> {t('game.victory.reopen')}
                        </button>
                    </div>
                )}
            </div>
            {/* Victory Overlay */}
            {gameStatus.won && showVictoryOverlay && createPortal(
                <div className="victory-overlay" onClick={() => setShowVictoryOverlay(false)}>
                    <div className="victory-overlay-content" ref={victoryDialogRef} role="dialog" aria-modal="true" aria-labelledby="victory-title"
                        onClick={(e) => e.stopPropagation()} onClickCapture={cancelAutoNext}
                        onKeyDownCapture={(e) => { if (e.key !== 'Enter' && e.key !== 'Tab') cancelAutoNext(); }}>
                        <button
                            className="victory-close-button"
                            onClick={() => setShowVictoryOverlay(false)}
                            aria-label={t('common.close')}
                        >
                            &#x2715;
                        </button>
                        <div className="victory-stars">
                            {renderStars(calculateStars(getUserSteps(), minSteps))}
                        </div>
                        <h2 className="victory-title" id="victory-title">{t('game.victory.title')}</h2>
                        <p className="victory-steps">
                            {t('game.victory.completedIn', { count: getUserSteps() })}
                            {minSteps && (
                                <span className="victory-optimal">
                                    {' '}{getUserSteps() === minSteps
                                        ? t('game.victory.perfect')
                                        : t('game.victory.optimal', { count: minSteps })}
                                </span>
                            )}
                        </p>
                        {victoryExtra}
                        {ladderPosition && ladderPosition.total > 0 && (
                            <p className="victory-progress">
                                {t(ladderPosition.done < ladderPosition.total ? 'game.victory.progressNext' : 'game.victory.progressComplete',
                                    { done: ladderPosition.done, total: ladderPosition.total })}
                            </p>
                        )}
                        {streak > 0 && (
                            <p className="victory-streak">
                                <span className="streak-fire">🔥</span> {t('game.victory.streak', { count: streak })}
                            </p>
                        )}
                        {pushAskVisible && (
                            <div className="victory-reminder-row">
                                <p className="victory-reminder-lead">{t('game.victory.reminderAsk')}</p>
                                <button
                                    className="victory-button reminder-button"
                                    onClick={async () => setReminderState(await requestPush('ladder_done'))}
                                >
                                    <span role="img" aria-label={t('game.victory.bell')}>🔔</span> {t('game.victory.remindMe')}
                                </button>
                                <button
                                    type="button"
                                    className="victory-inline-link"
                                    onClick={() => { recordPushPrompt({ step: 'dismiss', surface: 'ladder_done' }); markAsked(); }}
                                >
                                    {t('game.victory.reminderNotNow')}
                                </button>
                            </div>
                        )}
                        {appInviteVisible && (
                            <p className="victory-app-invite">
                                {t('game.victory.appInvite')}{' '}
                                <a
                                    href={STORE_URLS[appInviteOs]}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => recordAppInvite({ step: 'tap', os: appInviteOs })}
                                >
                                    {t(appInviteOs === 'ios' ? 'game.victory.appInviteIos' : 'game.victory.appInviteAndroid')}
                                </a>
                            </p>
                        )}
                        {!pushSupported && pushBlocker === 'needs_install' && !hasBeenAsked && !appInviteVisible && (
                            <p className="victory-reminder-confirm">
                                {t('game.victory.iosInstall')}{' '}
                                <a className="victory-inline-link" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer"
                                    onClick={() => { trackEvent('Get App Clicked', { context: 'victory_reminder' }); markAsked(); }}>
                                    {t('nav.getApp')}
                                </a>
                                <button type="button" className="victory-inline-link" onClick={markAsked}>{t('game.victory.gotIt')}</button>
                            </p>
                        )}
                        {reminderState && (
                            <p className={`victory-reminder-confirm${reminderState === 'subscribed' ? '' : ' is-muted'}`}>{t(
                                reminderState === 'subscribed' ? 'game.victory.reminderOn'
                                    : reminderState === 'dismissed' ? 'game.victory.reminderDeclined'
                                    : reminderState === 'denied' ? (isNative() ? 'game.victory.reminderBlockedApp' : 'game.victory.reminderBlocked')
                                    : 'game.victory.reminderFailed'
                            )}</p>
                        )}
                        {(pushEnabled || reminderState === 'subscribed') && (
                            <p className="victory-comeback">{t('game.victory.comeback')}</p>
                        )}
                        {achievements.length > 0 && (
                            <div className="victory-achievements">
                                {achievements.map(a => (
                                    <div key={a} className={`achievement-badge achievement-${a}`}>
                                        <FontAwesomeIcon icon={
                                            a === 'perfect_game' ? faBullseye :
                                            a === 'lightning_fast' ? faBolt :
                                            faFlagCheckered
                                        } />
                                        <span>{
                                            ['perfect_game', 'lightning_fast', 'marathon'].includes(a) ? t(`game.victory.achievements.${a}`) : ''
                                        }</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {ratingUpdate && <RatingChange ratingUpdate={ratingUpdate} />}
                        {rankProgressUpdate && rankProgressUpdate.starsEarnedThisGame > 0 && (
                            <div className="victory-rank-stars">
                                <span className="victory-stars-earned">
                                    +{rankProgressUpdate.starsEarnedThisGame}{' '}
                                    {[...Array(rankProgressUpdate.starsEarnedThisGame)].map((_, i) => (
                                        <FontAwesomeIcon key={i} icon={faStar} style={{ color: '#f0c040', fontSize: '0.85rem' }} />
                                    ))}
                                </span>
                            </div>
                        )}
                        {hintEarned && (
                            <div className="victory-hint-earned">
                                <FontAwesomeIcon icon={faLightbulb} />
                                <span>{t('game.victory.hintEarned', { count: hintEarned.hints })}</span>
                            </div>
                        )}
                        <div className="victory-share-row">
                            <button
                                className={`victory-button share-button ${shareStatus === 'copied' || shareStatus === 'shared' ? 'copied' : ''}`}
                                onClick={handleShare}
                                aria-busy={shareStatus === 'busy'}
                            >
                                <FontAwesomeIcon icon={shareStatus === 'copied' || shareStatus === 'shared' ? faCheck : faShareNodes} />
                                {shareStatus === 'copied' ? t('game.share.copied')
                                    : shareStatus === 'shared' ? t('game.share.sent')
                                    : t('game.share.button')}
                            </button>
                        </div>
                        <div className="victory-buttons">
                            {onRetry && (
                                <button className="victory-button retry-button" onClick={onRetry}>
                                    <FontAwesomeIcon icon={faArrowRotateRight} /> {t('game.victory.retry')}
                                </button>
                            )}
                            {mode === 'expert' && onExpertDifficulty ? (
                                <>
                                    <button className="victory-button easier-button" onClick={() => onExpertDifficulty('easier')}>
                                        <FontAwesomeIcon icon={faCircleMinus} /> {t('game.victory.easier')}
                                    </button>
                                    <button className="victory-button next-button" onClick={() => onExpertDifficulty('same')}>
                                        {t('common.next')} <FontAwesomeIcon icon={faArrowRight} />
                                    </button>
                                    <button className="victory-button harder-button" onClick={() => onExpertDifficulty('harder')}>
                                        <FontAwesomeIcon icon={faFire} /> {t('game.victory.harder')}
                                    </button>
                                </>
                            ) : onNextGame && canGoNext !== false ? (
                                <button className={`victory-button next-button${autoNext !== null ? ' counting' : ''}`} onClick={onNextGame} data-autofocus>
                                    {nextLabel || t(ladderPosition ? 'game.victory.nextPuzzle' : 'game.victory.nextGame')}{autoNext !== null ? ` (${autoNext})` : ''} <FontAwesomeIcon icon={faArrowRight} />
                                </button>
                            ) : null}
                        </div>
                        {autoNext !== null && (
                            <p className="victory-autonext">{t('game.victory.autoNext', { count: autoNext })}</p>
                        )}
                        {finalActions && !(onNextGame && canGoNext !== false) && mode !== 'expert' && (
                            <div className="victory-final-actions">
                                <p className="victory-final-title">{t('play.celebration.whatsNext')}</p>
                                {finalActions}
                            </div>
                        )}
                        {feedbackSlot}
                        <p className="victory-hint">{t('game.victory.viewSolution')}</p>
                    </div>
                </div>,
                portalRoot()
            )}
        </div>
    );
});

export default Game;
